import { CalendarCheck, ChevronLeft, ChevronRight, LogOut, Menu, Monitor, Moon, Search, Sun } from 'lucide-react'
import { format, addMonths, subMonths, parseISO } from 'date-fns'
import { fr } from 'date-fns/locale'
import { useAppStore } from '@/store/useAppStore'
import { useAuth } from '@/hooks/useAuth'
import { useDarkMode } from '@/hooks/useDarkMode'
import { getInitials } from '@/lib/utils'
import NotificationCenter from '@/components/ui/NotificationCenter'
import Tooltip from '@/components/ui/Tooltip'

interface TopBarProps {
  onMenuClick: () => void
  onSearchOpen: () => void
}

export default function TopBar({ onMenuClick, onSearchOpen }: TopBarProps) {
  const { selectedMonth, setSelectedMonth } = useAppStore()
  const { user, signOut } = useAuth()
  const { theme, cycleTheme } = useDarkMode()

  const currentDate  = parseISO(`${selectedMonth}-01`)
  const todayMonth   = format(new Date(), 'yyyy-MM')
  const isToday      = selectedMonth === todayMonth

  function prevMonth() {
    setSelectedMonth(format(subMonths(currentDate, 1), 'yyyy-MM'))
  }
  function nextMonth() {
    setSelectedMonth(format(addMonths(currentDate, 1), 'yyyy-MM'))
  }
  function goToToday() {
    setSelectedMonth(todayMonth)
  }

  const monthLabel = format(currentDate, 'MMMM yyyy', { locale: fr })

  return (
    <header className="h-14 border-b border-gray-100 dark:border-slate-800 bg-white dark:bg-slate-900 flex items-center gap-2 px-3 md:px-4 shrink-0">
      {/* Bouton hamburger — mobile uniquement */}
      <button
        onClick={onMenuClick}
        className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 dark:bg-gray-700 dark:hover:bg-slate-800 text-gray-500 dark:text-slate-400 transition md:hidden shrink-0"
        aria-label="Ouvrir le menu"
      >
        <Menu size={20} />
      </button>

      {/* Sélecteur de mois */}
      <div className="flex items-center gap-1">
        <Tooltip content="Mois précédent" position="bottom">
          <button
            onClick={prevMonth}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 dark:bg-gray-700 dark:hover:bg-slate-800 text-gray-500 dark:text-slate-400 transition"
          >
            <ChevronLeft size={16} />
          </button>
        </Tooltip>
        <span className="text-sm font-semibold text-gray-800 dark:text-slate-100 capitalize w-28 md:w-32 text-center">
          {monthLabel}
        </span>
        <Tooltip content="Mois suivant" position="bottom">
          <button
            onClick={nextMonth}
            className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 dark:bg-gray-700 dark:hover:bg-slate-800 text-gray-500 dark:text-slate-400 transition"
          >
            <ChevronRight size={16} />
          </button>
        </Tooltip>

        {/* Bouton retour mois courant — visible uniquement quand on navigue dans le passé/futur */}
        {!isToday && (
          <Tooltip content="Revenir au mois actuel" position="bottom">
            <button
              onClick={goToToday}
              className="ml-1 flex items-center gap-1 px-2 py-1 rounded-lg text-xs font-medium bg-indigo-50 dark:bg-indigo-900/30 text-[#4F46E5] dark:text-indigo-300 hover:bg-indigo-100 dark:hover:bg-indigo-900/50 transition"
            >
              <CalendarCheck size={12} />
              <span className="hidden sm:inline">Aujourd'hui</span>
            </button>
          </Tooltip>
        )}
      </div>

      {/* Bouton recherche globale */}
      <button
        onClick={onSearchOpen}
        className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-xl border border-gray-200 dark:border-slate-700 text-sm text-gray-400 dark:text-slate-500 hover:border-[#4F46E5] hover:text-[#4F46E5] transition bg-gray-50 dark:bg-slate-800 hover:bg-white dark:bg-gray-800 dark:hover:bg-slate-700"
      >
        <Search size={14} />
        <span>Rechercher…</span>
        <kbd className="ml-1 text-[10px] font-mono bg-gray-200 dark:bg-slate-700 text-gray-500 dark:text-slate-400 px-1.5 py-0.5 rounded-md">⌘K</kbd>
      </button>

      {/* Mobile search button */}
      <button
        onClick={onSearchOpen}
        className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 dark:bg-gray-700 dark:hover:bg-slate-800 text-gray-500 dark:text-slate-400 transition sm:hidden"
        aria-label="Recherche"
      >
        <Search size={18} />
      </button>

      <div className="flex-1" />

      {/* Toggle dark mode */}
      <Tooltip content={theme === 'dark' ? 'Thème sombre' : theme === 'light' ? 'Thème clair' : 'Thème système'} position="bottom">
        <button
          onClick={cycleTheme}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 text-gray-500 dark:text-slate-400 transition shrink-0"
          aria-label="Changer le thème"
        >
          {theme === 'dark' ? <Moon size={16} /> : theme === 'light' ? <Sun size={16} /> : <Monitor size={16} />}
        </button>
      </Tooltip>

      {/* Notifications */}
      <NotificationCenter />

      {/* Avatar + nom */}
      <div className="flex items-center gap-2">
        <div className="w-8 h-8 rounded-full bg-[#4F46E5] flex items-center justify-center shrink-0">
          <span className="text-xs font-bold text-white">
            {user?.full_name ? getInitials(user.full_name) : 'U'}
          </span>
        </div>
        {user && (
          <span className="text-sm font-medium text-gray-700 dark:text-slate-300 hidden sm:block truncate max-w-[120px]">
            {user.full_name}
          </span>
        )}
      </div>

      {/* Déconnexion */}
      <Tooltip content="Se déconnecter" position="bottom">
        <button
          onClick={signOut}
          className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-600 dark:bg-gray-700 dark:hover:bg-slate-800 text-gray-400 dark:text-slate-500 hover:text-gray-600 dark:text-gray-400 dark:hover:text-slate-300 transition shrink-0"
        >
          <LogOut size={16} />
        </button>
      </Tooltip>
    </header>
  )
}
